import { motion } from "framer-motion";
import { FileDown } from "lucide-react";
import { useLanguage } from "@/lang";
import { SectionHeading } from "./SectionHeading";

const Table = ({ title, rows, headers, testid }) => (
  <div data-testid={testid} className="mb-16">
    <h3 className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-6">{title}</h3>
    <div className="border-t border-l border-white/10">
      <div className="hidden md:grid grid-cols-12 border-b border-white/10 text-[11px] uppercase tracking-[0.2em] text-zinc-600">
        <span className="col-span-2 px-4 py-3 border-r border-white/10">{headers.year}</span>
        <span className="col-span-3 px-4 py-3 border-r border-white/10">{headers.role}</span>
        <span className="col-span-4 px-4 py-3 border-r border-white/10">{headers.production}</span>
        <span className="col-span-2 px-4 py-3 border-r border-white/10">{headers.director}</span>
        <span className="col-span-1 px-4 py-3">{headers.format}</span>
      </div>
      {rows.map((row, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: i * 0.06 }}
          data-testid={`${testid}-row-${i}`}
          className="grid grid-cols-2 md:grid-cols-12 border-b border-white/10 hover:bg-white/[0.03] transition-colors duration-300"
        >
          <span className="col-span-1 md:col-span-2 px-4 py-4 border-r border-white/10 text-sm text-zinc-500">{row.year}</span>
          <span className="col-span-1 md:col-span-3 px-4 py-4 border-r border-white/10 font-display text-lg tracking-tight">{row.role}</span>
          <span className="col-span-1 md:col-span-4 px-4 py-4 border-r border-white/10 text-sm text-zinc-300">{row.production}</span>
          <span className="col-span-1 md:col-span-2 px-4 py-4 border-r border-white/10 text-sm text-zinc-500">{row.director || "—"}</span>
          <span className="col-span-2 md:col-span-1 px-4 py-4 text-xs uppercase tracking-[0.15em] text-zinc-500">{row.type}</span>
        </motion.div>
      ))}
    </div>
  </div>
);

export const Vita = () => {
  const { copy } = useLanguage();
  const v = copy.vita;

  return (
  <section id="vita" data-testid="vita-section" className="border-y border-white/10 bg-[#0a0a0a]">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <SectionHeading {...copy.headings.vita} />

      <a
        data-testid="vita-pdf-download"
        href="/downloads/Clive_Bucher_Resume.pdf"
        download
        className="inline-flex items-center gap-3 -mt-6 mb-16 px-6 py-3 border border-white/20 text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-white hover:border-white/60 hover:-translate-y-0.5 transition-[color,border-color,transform] duration-300"
      >
        <FileDown size={15} /> {v.pdfLabel}
      </a>

      <div data-testid="vita-stats" className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-white/10 mb-20">
        {v.stats.map((s) => (
          <div key={s.label} className="px-5 py-6 border-b border-r border-white/10">
            <span className="block text-[11px] uppercase tracking-[0.25em] text-zinc-600 mb-2">{s.label}</span>
            <span className="font-display text-lg md:text-xl tracking-tight">{s.value}</span>
          </div>
        ))}
      </div>

      {v.tables.map((table) => (
        <Table key={table.key} title={table.title} rows={table.rows} headers={v.headers} testid={`vita-${table.key}`} />
      ))}

      <div data-testid="vita-skills" className="flex flex-wrap gap-3">
        {v.skills.map((skill) => (
          <span
            key={skill}
            className="px-4 py-2 border border-white/10 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:border-white/40 transition-[color,border-color] duration-300"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  </section>
  );
};
